import { type ClassValue, clsx } from "clsx";
import { env } from "~/env.mjs";

import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { type ProjectLocation } from "@prisma/client";

type TimerOptions = {
  threshold?: number;
  scope?: string;
};

class TimerContext {
  private scope = "global";
  private defaultThreshold: number | undefined;

  constructor(defaultThreshold?: number) {
    this.defaultThreshold = defaultThreshold;
  }

  setScope(scope: string) {
    this.scope = scope;
  }

  setDefaultThreshold(threshold: number) {
    this.defaultThreshold = threshold;
  }

  timeIt(label: string, options: TimerOptions = {}) {
    const start = process.hrtime.bigint();
    const { threshold = this.defaultThreshold, scope = this.scope } = options;

    return {
      end: () => {
        const end = process.hrtime.bigint();
        const duration = Number(end - start) / 1e6; // Convert to milliseconds

        const message = `[${scope}] ${label}: ${duration.toFixed(2)}ms`;

        if (threshold && duration > threshold) {
          console.warn(`${message} (exceeded threshold of ${threshold}ms)`);
        } else {
          console.log(message);
        }

        return duration;
      },
    };
  }
}

export const createTimerContext = (defaultThreshold?: number) =>
  new TimerContext(defaultThreshold);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function composeEventHandlers<E>(
  originalEventHandler?: (event: E) => void,
  ourEventHandler?: (event: E) => void,
  { checkForDefaultPrevented = true } = {},
) {
  return function handleEvent(event: E) {
    originalEventHandler?.(event);

    if (
      checkForDefaultPrevented === false ||
      !(event as unknown as Event).defaultPrevented
    ) {
      return ourEventHandler?.(event);
    }
  };
}

export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {},
) {
  return new Intl.DateTimeFormat("en-US", {
    month: options.month ?? "long",
    day: options.day ?? "numeric",
    year: options.year ?? "numeric",
    ...options,
  }).format(new Date(date));
}

export function assertNever(x: never): never {
  throw new Error(`Unexpected object: ${JSON.stringify(x)}`);
}

export const currency = "€";

export function absoluteUrl(path: string) {
  return `${env.NEXT_PUBLIC_DEPLOYMENT_URL}${path}`;
}

export function removeTrailingSlash(url: string) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytest")
      : (sizes[i] ?? "Bytes")
  }`;
}

export function getPlainUrl(fullUrl: string) {
  try {
    // Create a URL object
    const url = new URL(fullUrl);

    // Get the hostname (e.g., www.example.com or sub.example.com)
    const hostname = url.hostname;

    // Split the hostname into parts
    let parts = hostname.split(".");

    // Filter out common parts like 'www'
    parts = parts.filter((part) => part !== "www");

    // Join the remaining parts
    const plainUrl = parts.join(".");

    return plainUrl;
  } catch (error) {
    console.error("Invalid URL provided", error);
    return null;
  }
}

export const urlSchema = z.object({
  url: z
    .string()
    .refine(
      (value) => {
        // Check if the value contains a protocol
        const containsProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value);
        return containsProtocol || /^[^\s]+(\.[^\s]+)+$/.test(value);
      },
      {
        message: "Invalid URL format",
      },
    )
    .transform((value) => {
      // Prepend HTTPS protocol if it's missing or replace any existing protocol with HTTPS
      if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value)) {
        return `https://${value}`;
      }
      return value.replace(/^[a-zA-Z][a-zA-Z\d+\-.]*:/, "https:");
    }),
});

export const emailSchema = z.object({
  email: z.string().email(),
});

export const allowedOrgActionRoles = ["org:admin", "org:member"];

/** Admin, member, and team member roles that can build and purchase multiple Inbound GPT agents. */
export const allowedAgentBuilderRoles = [
  "org:admin",
  "org:member",
  "org:external_collaborator",
] as const;

export function canBuildAndPurchaseAgents(orgRole: string | null | undefined): boolean {
  if (!orgRole) return true;
  return (allowedAgentBuilderRoles as readonly string[]).includes(orgRole);
}

/** Primary entry point for creating another Inbound GPT agent from the dashboard. */
export const BUILD_INBOUND_AGENT_PATH = "/agents/hire/custom";

/** Starts a fresh build (clears cached wizard state via ?new=1 on the hire page). */
export const BUILD_NEW_INBOUND_AGENT_PATH = "/agents/hire/custom?new=1";
/** SiteGlue dashboard "+ Build new agent" → immersive `/onboard` flow. */
export const BUILD_NEW_SITEGLUE_AGENT_PATH = "/onboard?new=1";

// Define the structure of the response from IP2Location.io
export interface IP2LocationResponse {
  ip: string;
  country_code?: string;
  country_name?: string;
  region_name?: string;
  city_name?: string;
  latitude?: number;
  longitude?: number;
  zip_code?: string;
  time_zone?: string;
  asn?: string;
  as?: string;
  is_proxy?: boolean;
}

type NotificationKey =
  | "engagementAlerts"
  | "engagementReports"
  | "contactFormAlerts"
  | "abandonedConversationAlert";

export function hasNotificationEnabled(
  projectLocations: ProjectLocation[],
  key: NotificationKey
): boolean {
  console.log("🔍 DEBUG: hasNotificationEnabled called with key:", key);
  console.log("🔍 DEBUG: projectLocations count:", projectLocations.length);
  
  const result = projectLocations.some(location => {
    const specialist = location.specialist as any;
    const teamMembers = location.teamMembers as any[];

    console.log("🔍 DEBUG: Location:", location.name);
    console.log("🔍 DEBUG: Specialist notifications:", specialist?.notifications);
    console.log("🔍 DEBUG: Team members count:", teamMembers?.length || 0);
    
    if (teamMembers) {
      teamMembers.forEach((member, index) => {
        console.log(`🔍 DEBUG: Team member ${index} notifications:`, member?.notifications);
      });
    }

    const specialistHasKey = specialist?.notifications?.[key] !== false;
    const anyTeamMemberHasKey = teamMembers?.some(
      (member) => member?.notifications?.[key] !== false,
    );
    const hasLeadInbox =
      Array.isArray(location.toEmails) &&
      location.toEmails.some((email) => typeof email === "string" && email.trim().length > 0);

    // Default enabled (matches constructEmailRecipients). Require explicit false to disable.
    const locationEnabled =
      specialistHasKey ||
      anyTeamMemberHasKey ||
      (hasLeadInbox && specialist?.notifications?.[key] !== false);

    console.log("🔍 DEBUG: Specialist has key:", specialistHasKey);
    console.log("🔍 DEBUG: Any team member has key:", anyTeamMemberHasKey);
    console.log("🔍 DEBUG: Has lead toEmails:", hasLeadInbox);
    console.log("🔍 DEBUG: Location result:", locationEnabled);

    return locationEnabled;
  });
  
  console.log("🔍 DEBUG: Final hasNotificationEnabled result:", result);
  return result;
}